import React, { useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { useAppDispatch } from '../../../app/hooks';
import {
  FULLY_IMPLEMENTED,
  NOT_IMPLEMENTED,
  NOT_APPLICABLE,
} from '../../../services/surveyHelpers';
import { OrderedQuestionEntry } from './workspaceDerivations';
import { AsyncSaveButton } from '../../../components/buttons/AsyncSaveButton';
import { CURATED_TAGS, tagLabel, tagTheme } from './TagInput';
import { useSaveFinding } from './useSaveFinding';
import { updateAssessorAnswerTags } from '../../../services/assessments';
import { setQuestionTags } from '../../../app/store/questionnaireAnswerSlice';
import { ACCENT_BLUE, ACCENT_BLUE_DARK, SUCCESS_GREEN, ERROR_RED } from './qcTheme';

// Partially Implemented is excluded — per-control detail cannot apply uniformly across questions.
const BULK_OPTIONS: { label: string; c: string; rgb: string }[] = [
  { label: FULLY_IMPLEMENTED, c: SUCCESS_GREEN, rgb: '46,125,50' },
  { label: NOT_IMPLEMENTED, c: ERROR_RED, rgb: '198,40,40' },
  { label: NOT_APPLICABLE, c: '#616161', rgb: '97,97,97' },
];

const ModalHeader = styled('div')({
  padding: '24px 24px 8px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
});

const ModalTitle = styled('h2')({
  fontSize: '1.25rem',
  fontWeight: 500,
  color: 'rgba(0,0,0,0.87)',
  margin: 0,
});

const ModalSubtitle = styled('p')({
  fontSize: '0.875rem',
  color: 'rgba(0,0,0,0.6)',
  margin: '4px 0 0',
});

const CloseButton = styled('button')({
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  color: 'rgba(0,0,0,0.6)',
  padding: 0,
  display: 'inline-flex',
});

const SectionLabel = styled('p')({
  fontSize: '0.7rem',
  letterSpacing: '1px',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.6)',
  textTransform: 'uppercase',
  margin: '0 0 8px',
});

const LabelSuffix = styled('span')({
  fontWeight: 400,
  textTransform: 'none',
  letterSpacing: 0,
  color: 'rgba(0,0,0,0.5)',
});

const CountSuffix = styled('span')({
  color: 'rgba(0,0,0,0.4)',
  fontWeight: 700,
});

const Section = styled('div')({
  padding: '14px 24px 0',
});

const QuestionsList = styled('div')({
  border: '1px solid rgba(0,0,0,0.1)',
  borderRadius: '6px',
  overflow: 'auto',
  maxHeight: '224px',
});

const QuestionListRow = styled('div')({
  display: 'grid',
  gridTemplateColumns: '14px auto 1fr',
  columnGap: '6px',
  alignItems: 'start',
  padding: '11px 16px',
  borderBottom: '1px solid rgba(0,0,0,0.06)',
});

const RosetteSlot = styled('span')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  lineHeight: 1.5,
});

const RowCode = styled('span')({
  fontSize: '0.95rem',
  fontWeight: 700,
  color: ACCENT_BLUE_DARK,
  whiteSpace: 'nowrap',
  lineHeight: 1.5,
  marginRight: '10px',
});

const RowQuestion = styled('span')({
  fontSize: '0.95rem',
  color: 'rgba(0,0,0,0.78)',
  lineHeight: 1.5,
});

const OptionsStack = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const OptionRow = styled('button', {
  shouldForwardProp: prop => !['rgb', 'c', 'selected'].includes(prop as string),
})<{ rgb: string; c: string; selected: boolean }>(({ rgb, c, selected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '11px 14px',
  borderRadius: '10px',
  cursor: 'pointer',
  fontSize: '0.9rem',
  color: 'rgba(0,0,0,0.87)',
  fontFamily: 'inherit',
  textAlign: 'left',
  fontWeight: selected ? 600 : 400,
  background: selected ? `rgba(${rgb},0.14)` : '#f7f8fb',
  border: selected ? `2px solid ${c}` : '2px solid transparent',
}));

const OptionRing = styled('span', {
  shouldForwardProp: prop => !['c', 'selected'].includes(prop as string),
})<{ c: string; selected: boolean }>(({ c, selected }) => ({
  width: '18px',
  height: '18px',
  borderRadius: '50%',
  flex: 'none',
  border: selected ? `5px solid ${c}` : '2px solid rgba(0,0,0,0.4)',
  boxSizing: 'border-box',
}));

const FindingsTextarea = styled('textarea')({
  width: '100%',
  boxSizing: 'border-box',
  minHeight: '150px',
  border: '1px solid rgba(0,0,0,0.42)',
  borderRadius: '4px',
  padding: '10px 12px',
  fontFamily: 'Roboto, sans-serif',
  fontSize: '0.9rem',
  color: 'rgba(0,0,0,0.87)',
  resize: 'vertical',
  outline: 'none',
  '&:focus': {
    borderColor: ACCENT_BLUE,
    boxShadow: '0 0 0 2px rgba(25,118,210,0.2)',
  },
});

const TagChipRow = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
});

const BulkTagChip = styled('button', {
  shouldForwardProp: prop => !['rgb', 'tcolor', 'on'].includes(prop as string),
})<{ rgb: string; tcolor: string; on: boolean }>(({ rgb, tcolor, on }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  height: '28px',
  padding: '0 12px',
  borderRadius: '14px',
  fontSize: '0.78rem',
  cursor: 'pointer',
  fontFamily: 'inherit',
  background: on ? `rgba(${rgb},0.12)` : '#fff',
  color: on ? tcolor : 'rgba(0,0,0,0.72)',
  border: on ? `1px solid rgba(${rgb},0.6)` : '1px solid rgba(0,0,0,0.15)',
  fontWeight: on ? 600 : 500,
  '& .MuiSvgIcon-root': {
    fontSize: '15px',
  },
}));

const TagDot = styled('span', {
  shouldForwardProp: prop => prop !== 'dot',
})<{ dot: string }>(({ dot }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  background: dot,
  flex: 'none',
}));

const ModalFooter = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  padding: '24px',
});

const CancelButton = styled('button')({
  background: '#fff',
  color: ACCENT_BLUE,
  border: 'none',
  borderRadius: '4px',
  padding: '10px 16px',
  fontSize: '0.875rem',
  fontWeight: 600,
  letterSpacing: '0.4px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': {
    background: 'rgba(25,118,210,0.04)',
  },
});

// AsyncSaveButton (spinner while saving) dressed to match the modal footer.
const ApplyButton = styled(AsyncSaveButton)({
  padding: '10px 20px',
  fontSize: '0.875rem',
  fontWeight: 600,
  letterSpacing: '0.4px',
  fontFamily: 'inherit',
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
});

const ErrorLine = styled('p')({
  fontSize: '0.78rem',
  color: ERROR_RED,
  margin: '8px 24px 0',
});

export interface BulkUpdateModalProps {
  open: boolean;
  entries: OrderedQuestionEntry[];
  onApplied: () => void;
  onClose: () => void;
}

export default function BulkUpdateModal({ open, entries, onApplied, onClose }: BulkUpdateModalProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { saveFinding } = useSaveFinding();
  const [answer, setAnswer] = useState('');
  const [note, setNote] = useState('');
  const [bulkTags, setBulkTags] = useState<string[]>([]);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');

  const allTagValues = useMemo(() => CURATED_TAGS.map(t => t.value), []);
  const canApply = !!answer && !!note.trim() && !applying;

  // Union with the tags the save just reported — a bulk apply never clears an individually-set tag.
  const applyTags = async (questionId: number, answerId: number, baseTags: string[]) => {
    const merged = Array.from(new Set([...baseTags, ...bulkTags]));
    try {
      await updateAssessorAnswerTags(answerId, merged);
      dispatch(setQuestionTags({ questionId, tags: merged }));
    } catch (tagError) {
      console.error('Failed to apply bulk finding tags', tagError);
    }
  };

  const reset = () => {
    setAnswer('');
    setNote('');
    setBulkTags([]);
    setError('');
  };

  const handleClose = () => {
    if (applying) return;
    reset();
    onClose();
  };

  // Click-driven bulk apply: each question runs the same save flow as a single Save Finding, so each counts complete immediately.
  const handleApply = async () => {
    if (!canApply) return;
    setApplying(true);
    setError('');
    const failures: string[] = [];
    for (const entry of entries) {
      // eslint-disable-next-line no-await-in-loop
      const result = await saveFinding({
        question: entry.question,
        assessorAnswer: answer,
        assessorFinding: note,
        questionCtrlAns: {},
      });
      if (result.ok) {
        if (bulkTags.length && result.answerId) await applyTags(entry.question.id, result.answerId, result.tags || []);
      } else {
        failures.push(entry.question.shortCode || String(entry.question.id));
      }
    }
    setApplying(false);
    if (failures.length) {
      setError(`Could not save ${failures.join(', ')}. Review those questions individually.`);
      return;
    }
    reset();
    onApplied();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-label='Bulk update findings'
      PaperProps={{ sx: { width: 1000, maxWidth: 'calc(100% - 32px)', borderRadius: '4px' } }}
      maxWidth={false}
    >
      <ModalHeader>
        <div>
          <ModalTitle>Bulk Update Findings</ModalTitle>
          <ModalSubtitle>Apply one assessor answer to all selected questions.</ModalSubtitle>
        </div>
        <CloseButton type='button' aria-label='Close bulk update' onClick={handleClose}>
          <CloseIcon />
        </CloseButton>
      </ModalHeader>
      <Section>
        <SectionLabel>Questions - <CountSuffix>{entries.length}</CountSuffix></SectionLabel>
        <QuestionsList>
          {entries.map(entry => (
            <QuestionListRow key={entry.question.id}>
              <RosetteSlot>
                {entry.question.isBestPractice && <WorkspacePremiumIcon sx={{ fontSize: 15, color: '#D467E4' }} />}
              </RosetteSlot>
              <RowCode>{entry.question.shortCode}</RowCode>
              <RowQuestion>{entry.question.title}</RowQuestion>
            </QuestionListRow>
          ))}
        </QuestionsList>
      </Section>
      <Section sx={{ paddingTop: '20px' }}>
        <SectionLabel sx={{ marginBottom: '10px' }}>Assessor Answer</SectionLabel>
        <OptionsStack>
          {BULK_OPTIONS.map(option => (
            <OptionRow
              key={option.label}
              type='button'
              rgb={option.rgb}
              c={option.c}
              selected={answer === option.label}
              role='radio'
              aria-checked={answer === option.label}
              onClick={() => setAnswer(option.label)}
            >
              <OptionRing c={option.c} selected={answer === option.label} />
              {option.label}
            </OptionRow>
          ))}
        </OptionsStack>
      </Section>
      <Section sx={{ paddingTop: '20px' }}>
        <SectionLabel>Assessor Findings <LabelSuffix>(applied to all)</LabelSuffix></SectionLabel>
        <FindingsTextarea
          value={note}
          placeholder='Add a note that applies to every selected question.'
          onChange={e => setNote(e.target.value)}
        />
      </Section>
      <Section sx={{ paddingTop: '12px' }}>
        <SectionLabel>Tags <LabelSuffix>(optional, applied to all)</LabelSuffix></SectionLabel>
        <TagChipRow>
          {allTagValues.map(name => {
            const on = bulkTags.includes(name);
            const theme = tagTheme(name);
            return (
              <BulkTagChip
                key={name}
                type='button'
                rgb={theme.rgb}
                tcolor={theme.text}
                on={on}
                onClick={() => setBulkTags(prev => (on ? prev.filter(t => t !== name) : [...prev, name]))}
              >
                {on ? <CheckIcon /> : <TagDot dot={theme.text} />}
                {tagLabel(name)}
              </BulkTagChip>
            );
          })}
        </TagChipRow>
      </Section>
      {error && <ErrorLine>{error}</ErrorLine>}
      <ModalFooter>
        <CancelButton type='button' disabled={applying} onClick={handleClose}>Cancel</CancelButton>
        <ApplyButton
          variant='contained'
          disableElevation
          isSaving={applying}
          disabled={!canApply}
          label={`Apply to ${entries.length}`}
          savingLabel='Applying…'
          onClick={handleApply}
        />
      </ModalFooter>
    </Dialog>
  );
}
